import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Task } from '../types/task';
import type { Workstream } from '../types/workstream';

interface ParallelToggleProps {
  task: Task;
  workstreamID: string;
  workstream: Workstream | null;
  onSaved?: (updated: { Parallel: boolean; Version: number }) => void;
}

const MAX_PARALLEL_DISPLAY = 10;
// Fetch one extra to detect overflow ("..." indicator).
const FETCH_LIMIT = MAX_PARALLEL_DISPLAY + 1;

function formatTaskRef(t: Task, ws: Workstream | null): string {
  const num = t.TaskNumber;
  if (!num) return t.Title;
  if (ws?.defaultShortName) return `${t.Title} (${ws.defaultShortName.toUpperCase()}-${num})`;
  return `${t.Title} (${num})`;
}

// Collect contiguous parallel tasks from a page, stopping at the first
// non-parallel task.
function collectParallel(tasks: Task[]): Task[] {
  const result: Task[] = [];
  for (const t of tasks) {
    if (!t.Parallel) break;
    result.push(t);
  }
  return result;
}

export default function ParallelToggle({ task, workstreamID, workstream, onSaved }: ParallelToggleProps) {
  const [saving, setSaving] = useState(false);
  const [parallelTasks, setParallelTasks] = useState<Task[]>([]);

  const isParallel = task.Parallel ?? false;

  // Fetch parallel siblings using beforeTaskId/afterTaskId to avoid
  // loading the entire workstream. We fetch a small page in each
  // direction and stop at the first non-parallel task.
  useEffect(() => {
    if (!isParallel) {
      setParallelTasks([]);
      return;
    }
    let cancelled = false;
    const wsBase = `/api/workstreams/${encodeURIComponent(workstreamID)}`;

    Promise.all([
      fetch(`${wsBase}/tasks?beforeTaskId=${encodeURIComponent(task.TaskID)}&maxResults=${FETCH_LIMIT}`),
      fetch(`${wsBase}/tasks?afterTaskId=${encodeURIComponent(task.TaskID)}&maxResults=${FETCH_LIMIT}`),
    ]).then(async ([beforeResp, afterResp]) => {
      if (cancelled) return;

      let before: Task[] = [];
      let after: Task[] = [];

      if (beforeResp.ok) {
        const data: { Tasks: Task[] } = await beforeResp.json();
        before = collectParallel(data.Tasks ?? []).reverse();
      }

      if (afterResp.ok) {
        const data: { Tasks: Task[] } = await afterResp.json();
        after = collectParallel(data.Tasks ?? []);
      }

      if (!cancelled) {
        setParallelTasks([...before, ...after]);
      }
    }).catch(() => {});

    return () => { cancelled = true; };
  }, [isParallel, task.TaskID, workstreamID]);

  const handleToggle = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    const newValue = !isParallel;
    try {
      const resp = await fetch(
        `/api/workstreams/${encodeURIComponent(workstreamID)}/tasks/${encodeURIComponent(task.TaskID)}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Version: task.Version ?? 1, Fields: { Parallel: newValue } }),
        },
      );
      if (resp.ok) {
        const data = await resp.json();
        onSaved?.({ Parallel: data.Parallel ?? newValue, Version: data.Version });
      }
    } catch {
      // silently fail
    } finally {
      setSaving(false);
    }
  }, [saving, isParallel, workstreamID, task.TaskID, task.Version, onSaved]);

  const displayTasks = parallelTasks.slice(0, MAX_PARALLEL_DISPLAY);
  const hasMore = parallelTasks.length > MAX_PARALLEL_DISPLAY;

  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '14px', fontWeight: 500 }}>Enable Parallel Execution</span>
        <button
          type="button"
          role="switch"
          aria-checked={isParallel}
          onClick={handleToggle}
          disabled={saving}
          style={{
            width: '40px',
            height: '22px',
            borderRadius: '11px',
            border: 'none',
            padding: '2px',
            cursor: saving ? 'wait' : 'pointer',
            backgroundColor: isParallel ? '#28a745' : '#ccc',
            transition: 'background-color 0.2s',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              width: '18px',
              height: '18px',
              borderRadius: '50%',
              backgroundColor: '#fff',
              transition: 'transform 0.2s',
              transform: isParallel ? 'translateX(18px)' : 'translateX(0)',
              display: 'block',
            }}
          />
        </button>
      </div>

      {isParallel && displayTasks.length > 0 && (
        <div
          style={{
            marginTop: '10px',
            border: '1px solid #ccc',
            borderRadius: '8px',
            padding: '12px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            alignItems: 'center',
          }}
        >
          {displayTasks.map((t) => (
            <Link
              key={t.TaskID}
              to={`/workstreams/tasks/${encodeURIComponent(workstreamID)}/${encodeURIComponent(t.TaskID)}`}
              style={{
                display: 'inline-block',
                padding: '4px 12px',
                borderRadius: '6px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffc107',
                color: '#24292f',
                fontSize: '13px',
                fontWeight: 500,
                textDecoration: 'none',
              }}
            >
              {formatTaskRef(t, workstream)}
            </Link>
          ))}
          {hasMore && (
            <span style={{ fontSize: '13px', color: '#656d76' }}>...</span>
          )}
        </div>
      )}
    </div>
  );
}
